import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUserPlus,
  FaEnvelope,
  FaUser,
  FaUserShield,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaSyncAlt,
  FaCopy,
  FaCheck,
  FaSearch,
  FaChevronDown,
  FaChevronRight,
  FaUsersCog,
  FaCircleNotch,
  FaTimes,
  FaExclamationTriangle,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import { ROLE_LABELS, assignableRoles } from "../../constants/roles";
import "./UserManagement.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const getAuthToken = () =>
  sessionStorage.getItem("auth_token") ||
  localStorage.getItem("token") ||
  localStorage.getItem("accessToken") ||
  sessionStorage.getItem("auth_token") ||
  sessionStorage.getItem("token") ||
  sessionStorage.getItem("accessToken") ||
  "";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getAuthToken()}`,
});

const apiCall = async (path, options = {}) => {
  const resp = await fetch(`${API_BASE}${path}`, options);
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok || !data?.success) {
    throw new Error(data?.message || data?.error || `Request failed (${resp.status}).`);
  }
  return data;
};

const normEmail = (e) => String(e || "").trim().toLowerCase();
const roleClass = (role) => `role-${String(role || "analyst").replace(/_/g, "-")}`;

const initialsOf = (name, email) => {
  const base = String(name || "").trim();
  if (base) {
    const parts = base.split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return base.slice(0, 2).toUpperCase();
  }
  const handle = String(email || "?").split("@")[0];
  return handle.slice(0, 2).toUpperCase();
};

const generatePassword = (len = 16) => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*?";
  const crypto = window.crypto || window.msCrypto;
  let out = "";
  if (crypto?.getRandomValues) {
    const buf = new Uint32Array(len);
    crypto.getRandomValues(buf);
    for (let i = 0; i < len; i++) out += chars[buf[i] % chars.length];
  } else {
    for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
};

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : "—";

const PAGE_SIZE = 12;

const UserManagement = ({ embedded = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const roleOptions = useMemo(
    () => assignableRoles(user?.role).map((v) => ({ value: v, label: ROLE_LABELS[v] })),
    [user]
  );
  const defaultAssignable = roleOptions[0]?.value || "analyst";

  const [users, setUsers] = useState(null);
  const [loadingList, setLoadingList] = useState(false);

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);

  // Add-user modal
  const [showAdd, setShowAdd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addName, setAddName] = useState("");
  const [addRole, setAddRole] = useState(defaultAssignable);
  const [addPw, setAddPw] = useState("");
  const [showAddPw, setShowAddPw] = useState(false);
  const [copied, setCopied] = useState(false);

  const [okMsg, setOkMsg] = useState("");
  const [errMsg, setErrMsg] = useState("");

  const loadUsers = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setErrMsg("You are not logged in. Please login again.");
      return;
    }
    setLoadingList(true);
    try {
      const data = await apiCall(`/api/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(data.users || []);
    } catch (err) {
      setErrMsg(err.message || "Failed to load directory.");
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const copyToClipboard = useCallback((value) => {
    if (!value) return;
    try {
      navigator.clipboard?.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard unavailable */
    }
  }, []);

  const filtered = useMemo(() => {
    if (!users) return [];
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (q) {
        const hay = `${u.name || ""} ${u.email || ""} ${u.role || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (roleFilter !== "all" && String(u.role || "").toLowerCase() !== roleFilter) return false;
      if (statusFilter === "active" && Number(u.disabled) === 1) return false;
      if (statusFilter === "disabled" && Number(u.disabled) !== 1) return false;
      if (statusFilter === "temp" && Number(u.must_change_password) !== 1) return false;
      return true;
    });
  }, [users, search, roleFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = useMemo(
    () => filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filtered, safePage]
  );

  useEffect(() => {
    setPage(1);
  }, [search, roleFilter, statusFilter]);

  const stats = useMemo(() => {
    const list = users || [];
    return {
      total: list.length,
      active: list.filter((u) => Number(u.disabled) !== 1).length,
      disabled: list.filter((u) => Number(u.disabled) === 1).length,
      temp: list.filter((u) => Number(u.must_change_password) === 1).length,
    };
  }, [users]);

  const openAdd = () => {
    setOkMsg("");
    setErrMsg("");
    setAddEmail("");
    setAddName("");
    setAddRole(defaultAssignable);
    setAddPw("");
    setShowAddPw(false);
    setCopied(false);
    setShowAdd(true);
  };
  const closeAdd = () => {
    if (busy) return;
    setShowAdd(false);
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    const em = addEmail.trim();
    if (em.length < 5 || !em.includes("@") || addName.trim().length < 2 || addPw.trim().length < 8) {
      setErrMsg("Enter a valid email, a name, and a temporary password (min 8 chars).");
      return;
    }
    setBusy(true);
    try {
      const data = await apiCall(`/api/admin/users`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ email: em, name: addName.trim(), role: addRole, tempPassword: addPw.trim() }),
      });
      setUsers((prev) => [data.user, ...(prev || [])]);
      setOkMsg(`${data.user?.name || em} created. They must change password at first login.`);
      setShowAdd(false);
    } catch (err) {
      setErrMsg(err.message || "Failed to create user.");
    } finally {
      setBusy(false);
    }
  };

  const isSelf = (u) => normEmail(u.email) === normEmail(user?.email);
  const openUser = (u) => navigate(`/admin/users/${u.id}`);

  return (
    <div className={`um-page${embedded ? " um-embedded" : ""}`}>
      <div className="um-card">
        {/* Header */}
        <div className="um-header">
          <div className="um-header-row">
            <div className="um-header-icon">
              <FaUsersCog />
            </div>
            <div className="um-header-text">
              <h1 className="um-title">User Management</h1>
              <p className="um-subtitle">
                Select a user to manage their profile, role, password, and account access —
                or onboard a new analyst.
              </p>
            </div>
            <button type="button" className="um-btn primary um-header-cta" onClick={openAdd}>
              <FaUserPlus /> Add User
            </button>
          </div>
        </div>

        {/* Toasts */}
        {okMsg ? (
          <div className="um-alert ok">
            <FaCheck className="um-alert-icon" />
            <span>{okMsg}</span>
            <button type="button" className="um-alert-x" onClick={() => setOkMsg("")}><FaTimes /></button>
          </div>
        ) : null}
        {errMsg ? (
          <div className="um-alert err">
            <FaExclamationTriangle className="um-alert-icon" />
            <span>{errMsg}</span>
            <button type="button" className="um-alert-x" onClick={() => setErrMsg("")}><FaTimes /></button>
          </div>
        ) : null}

        {/* Stat tiles (also act as status filters) */}
        <div className="um-stats">
          <button type="button" className={`um-stat ${statusFilter === "all" ? "active" : ""}`} onClick={() => setStatusFilter("all")}>
            <span className="um-stat-num">{stats.total}</span>
            <span className="um-stat-lbl">Total users</span>
          </button>
          <button type="button" className={`um-stat ${statusFilter === "active" ? "active" : ""}`} onClick={() => setStatusFilter("active")}>
            <span className="um-stat-num">{stats.active}</span>
            <span className="um-stat-lbl">Active</span>
          </button>
          <button type="button" className={`um-stat ${statusFilter === "disabled" ? "active" : ""}`} onClick={() => setStatusFilter("disabled")}>
            <span className="um-stat-num">{stats.disabled}</span>
            <span className="um-stat-lbl">Disabled</span>
          </button>
          <button type="button" className={`um-stat ${statusFilter === "temp" ? "active" : ""}`} onClick={() => setStatusFilter("temp")}>
            <span className="um-stat-num">{stats.temp}</span>
            <span className="um-stat-lbl">Temp password</span>
          </button>
        </div>

        {/* Toolbar */}
        <div className="um-toolbar">
          <div className="um-input um-search">
            <FaSearch className="um-input-icon" />
            <input
              type="text"
              placeholder="Search by name, email or role…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="um-input um-select um-filter">
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} aria-label="Filter by role">
              <option value="all">All roles</option>
              {Object.entries(ROLE_LABELS).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
            <FaChevronDown className="um-select-caret" />
          </div>
          <button type="button" className="um-btn secondary" onClick={loadUsers} disabled={loadingList} title="Refresh">
            <FaSyncAlt className={loadingList ? "um-spin" : ""} /> Refresh
          </button>
        </div>

        {/* Table */}
        <div className="um-table-wrap">
          <table className="um-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th>Joined</th>
                <th aria-label="Open" />
              </tr>
            </thead>
            <tbody>
              {loadingList && !users ? (
                <tr>
                  <td colSpan={5} className="um-table-state">
                    <FaCircleNotch className="um-spin" /> Loading directory…
                  </td>
                </tr>
              ) : paged.length === 0 ? (
                <tr>
                  <td colSpan={5} className="um-table-state">
                    {users && users.length > 0 ? "No users match the current filters." : "No users yet."}
                  </td>
                </tr>
              ) : (
                paged.map((u) => {
                  const disabled = Number(u.disabled) === 1;
                  return (
                    <tr
                      key={u.id}
                      className={`um-row-link ${disabled ? "um-row-disabled" : ""}`}
                      onClick={() => openUser(u)}
                      tabIndex={0}
                      role="button"
                      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), openUser(u))}
                    >
                      <td>
                        <div className="um-identity">
                          <span className={`um-avatar sm ${roleClass(u.role)}`}>{initialsOf(u.name, u.email)}</span>
                          <span className="um-identity-text">
                            <span className="um-identity-name">
                              {u.name || "Unnamed user"}
                              {isSelf(u) ? <span className="um-you">You</span> : null}
                            </span>
                            <span className="um-identity-email">{u.email}</span>
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`um-badge ${roleClass(u.role)}`}>
                          {ROLE_LABELS[u.role] || u.role || "Analyst"}
                        </span>
                      </td>
                      <td>
                        <div className="um-status-cell">
                          <span className={`um-status ${disabled ? "is-disabled" : "is-active"}`}>
                            <span className="um-status-dot" />
                            {disabled ? "Disabled" : "Active"}
                          </span>
                          {Number(u.must_change_password) === 1 ? (
                            <span className="um-badge temp" title="Awaiting first-login password change">Temp pw</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="um-cell-date">{fmtDate(u.created_at)}</td>
                      <td className="um-cell-chevron"><FaChevronRight /></td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer / pager */}
        {filtered.length > 0 ? (
          <div className="um-foot">
            <span className="um-foot-info">
              {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
            {totalPages > 1 ? (
              <div className="um-pager">
                <button type="button" className="um-pager-btn" onClick={() => setPage(1)} disabled={safePage === 1}>«</button>
                <button type="button" className="um-pager-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1}>‹</button>
                <span className="um-pager-pos">{safePage} / {totalPages}</span>
                <button type="button" className="um-pager-btn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}>›</button>
                <button type="button" className="um-pager-btn" onClick={() => setPage(totalPages)} disabled={safePage === totalPages}>»</button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* ===================== ADD USER MODAL ===================== */}
      {showAdd ? (
        <div className="um-modal-backdrop" onMouseDown={closeAdd}>
          <div className="um-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <form onSubmit={submitAdd}>
              <div className="um-modal-head">
                <div className="um-modal-title"><FaUserPlus /> Add User</div>
                <button type="button" className="um-modal-x" onClick={closeAdd}><FaTimes /></button>
              </div>
              <div className="um-modal-body">
                <div className="um-modal-grid">
                  <div className="um-field">
                    <label>Email address</label>
                    <div className="um-input">
                      <FaEnvelope className="um-input-icon" />
                      <input type="email" value={addEmail} placeholder="user@accenture.com" onChange={(e) => setAddEmail(e.target.value)} autoComplete="off" autoFocus />
                    </div>
                  </div>
                  <div className="um-field">
                    <label>Full name</label>
                    <div className="um-input">
                      <FaUser className="um-input-icon" />
                      <input type="text" value={addName} placeholder="John Doe" onChange={(e) => setAddName(e.target.value)} autoComplete="off" />
                    </div>
                  </div>
                  <div className="um-field">
                    <label>Role</label>
                    <div className="um-input um-select">
                      <FaUserShield className="um-input-icon" />
                      <select value={addRole} onChange={(e) => setAddRole(e.target.value)}>
                        {roleOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <FaChevronDown className="um-select-caret" />
                    </div>
                  </div>
                  <div className="um-field">
                    <label>Temporary password</label>
                    <div className="um-input">
                      <FaLock className="um-input-icon" />
                      <input
                        type={showAddPw ? "text" : "password"}
                        value={addPw}
                        placeholder="Min 8 characters"
                        onChange={(e) => setAddPw(e.target.value)}
                        autoComplete="new-password"
                      />
                      <div className="um-input-tools">
                        <button type="button" className="um-icon-btn" title="Generate strong password" onClick={() => { setAddPw(generatePassword()); setShowAddPw(true); }}><FaSyncAlt /></button>
                        <button type="button" className="um-icon-btn" title={copied ? "Copied" : "Copy"} disabled={!addPw} onClick={() => copyToClipboard(addPw)}>{copied ? <FaCheck /> : <FaCopy />}</button>
                        <button type="button" className="um-icon-btn" title={showAddPw ? "Hide" : "Show"} onClick={() => setShowAddPw((s) => !s)}>{showAddPw ? <FaEyeSlash /> : <FaEye />}</button>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="um-hint">The user will be prompted to set their own password on first login.</p>
              </div>
              <div className="um-modal-foot">
                <button type="button" className="um-btn secondary" onClick={closeAdd} disabled={busy}>Cancel</button>
                <button type="submit" className="um-btn primary" disabled={busy}>
                  {busy ? <><FaCircleNotch className="um-spin" /> Creating…</> : <><FaUserPlus /> Create User</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default UserManagement;
